import React from "react";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Switch,
  Button,
  Divider,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import { useSupabase } from "../providers/SupabaseProvider";
import Page from "../components/Page";

const Settings: React.FC = () => {
  const { signOut, user, linkAnonymousWithEmailPassword, loading } =
    useSupabase();
  const [darkMode, setDarkMode] = React.useState(false);
  const [showUpgrade, setShowUpgrade] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [successMsg, setSuccessMsg] = React.useState<string | null>(null);

  const handleUpgrade = async () => {
    try {
      setError(null);
      setSuccessMsg(null);
      const status = await linkAnonymousWithEmailPassword(email, password);
      if (status === "verify_email") {
        setSuccessMsg(
          "Check your email to confirm your address. After verifying, reopen this screen to set your password.",
        );
      } else {
        setSuccessMsg("Account linked successfully!");
        setShowUpgrade(false);
      }
      // Keep the upgrade form open so user sees message
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <Page title="Settings">
      <Box sx={{ p: 3 }}>
        <List>
          <ListItem>
            <ListItemText primary="Dark Mode" />
            <Switch
              checked={darkMode}
              onChange={(e) => setDarkMode(e.target.checked)}
            />
          </ListItem>
          <Divider />
          {user?.isAnonymous && (
            <>
              <ListItem>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => setShowUpgrade(true)}
                  disabled={loading}
                >
                  Save game progress
                </Button>
              </ListItem>
              {showUpgrade && (
                <Box sx={{ width: "100%", p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Create Your Account
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Link your progress to a permanent account
                  </Typography>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    sx={{ mb: 2 }}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <TextField
                    fullWidth
                    label="Password"
                    type="password"
                    sx={{ mb: 2 }}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}
                  {successMsg && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {successMsg}
                    </Alert>
                  )}
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleUpgrade}
                    disabled={loading}
                  >
                    {loading ? "Creating account..." : "Create account"}
                  </Button>
                </Box>
              )}
              <Divider />
            </>
          )}
          <ListItem>
            <Button
              color="error"
              onClick={() => signOut()}
              fullWidth
              variant="text"
              sx={{ justifyContent: "flex-start" }}
            >
              Sign Out
            </Button>
          </ListItem>
        </List>
      </Box>
    </Page>
  );
};

export default Settings;
